// TIP-615 — Become a Member, from the Counterparty Briefs page.
//
// A SECOND ENTRY POINT, NOT A SECOND PRODUCT. Same Stripe price IDs, same
// CompanyMembership row, same webhook handlers, same provisioning — it posts
// to the very endpoint the dashboard's membership screen posts to
// (/api/billing/verified-checkout/) with the same `plan` values.
//
// Design: `Checkout Flow.dc.html` sections 1a-1f. Same shell and the same
// Stripe block as the buy-briefs step; what differs is step 1, because
// choosing between two plans is not the same shape as choosing a quantity.
//
// The allowance effect afterwards is a no-op in code, which is the payoff of
// deriving the limit rather than storing it: the moment CompanyMembership goes
// active, Company.is_member is true and the cap reads 30 with no weekly limit.
// Nothing here has to teach the page that.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./counterpartyBriefs.css";
import StripeCheckoutPanel from "../../../common/StripeCheckoutPanel";
import {
  MEMBERSHIP_COPY,
  MEMBERSHIP_PLAN_ORDER,
  planQuote,
} from "./membershipCheckoutCopy";
import { formatAed } from "../../../../constants/counterpartyBriefPricing";

/**
 * The plan's price, excluding tax. No tax line — see BuyBriefsModal's
 * OrderSummary: Stripe computes it from the billing address it collects.
 */
const PlanSummary = ({ planId }) => {
  const priced = planQuote(planId);
  if (!priced) return null;
  const monthly = planId === "monthly";

  return (
    <div className="cb-buy-summary">
      <div className="cb-buy-row">
        <span className="cb-buy-row-label">{MEMBERSHIP_COPY.lineLabel(planId)}</span>
        <span className="cb-buy-row-value" data-testid="cb-member-net">
          {formatAed(priced.netFils)}
        </span>
      </div>

      <div className="cb-buy-total">
        <span className="cb-buy-total-label">
          {monthly ? MEMBERSHIP_COPY.totalTodayLabel : MEMBERSHIP_COPY.totalLabel}{" "}
          <span className="cb-buy-extax">{MEMBERSHIP_COPY.excludingTax}</span>
        </span>
        <span className="cb-buy-total-value" data-testid="cb-member-total">
          {formatAed(priced.netFils)}
        </span>
      </div>

      {monthly && (
        <p className="cb-member-renewal" data-testid="cb-member-renewal">
          {MEMBERSHIP_COPY.renewalNote}
        </p>
      )}
    </div>
  );
};

const MembershipModal = ({
  // The business being made a member. Its name heads the modal.
  companyName = null,
  // The member per-brief price, from the allowance endpoint's pricing block.
  // Used only in step 2's note; absent, the sentence drops that clause rather
  // than quoting a price we do not have.
  memberUnit = null,
  onClose,
  // async ({ plan }) => { clientSecret } | { error } | { alreadyMember: true }
  onCreateSession,
  // Called once Stripe reports the payment complete without redirecting.
  onPaid,
}) => {
  // Annual preselected, as the design draws it.
  const [plan, setPlan] = useState("yearly");
  const [clientSecret, setClientSecret] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [done, setDone] = useState(false);

  const step = useMemo(() => {
    if (done) return "done";
    if (alreadyMember) return "conflict";
    if (error) return "error";
    if (clientSecret || creating) return "payment";
    return "plan";
  }, [done, alreadyMember, error, clientSecret, creating]);

  const start = useCallback(async () => {
    setError(null);
    setCreating(true);
    const result = await onCreateSession({ plan });
    setCreating(false);

    // A member reaching this modal is TOLD, never sold to twice.
    if (result?.alreadyMember) {
      setAlreadyMember(true);
      return;
    }
    if (result?.clientSecret) {
      setClientSecret(result.clientSecret);
      return;
    }
    setError(result?.error || { message: MEMBERSHIP_COPY.errorBody });
  }, [onCreateSession, plan]);

  // Changing plan discards the session: its line item is the plan, fixed when
  // the session was created, so reusing it would subscribe to the old one.
  const back = useCallback(() => {
    setClientSecret(null);
    setError(null);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);


  return (
    <div className="cb-modal-backdrop cb-modal-backdrop--sheet" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={MEMBERSHIP_COPY.eyebrow}
        data-testid="cb-member-modal"
        data-step={step}
        className="cb-modal cb-buy-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {step === "plan" && (
          <>
            <div className="cb-modal-head">
              <div>
                <p className="cb-modal-eyebrow">{MEMBERSHIP_COPY.eyebrow}</p>
                <h3 className="cb-modal-title">{MEMBERSHIP_COPY.title(companyName)}</h3>
                <p className="cb-buy-sub">{MEMBERSHIP_COPY.sub}</p>
              </div>
              <button
                type="button"
                className="cb-modal-close"
                onClick={onClose}
                aria-label={MEMBERSHIP_COPY.close}
              >
                &#215;
              </button>
            </div>

            <div className="cb-buy-body">
              <div className="cb-member-plans" role="radiogroup" aria-label={MEMBERSHIP_COPY.eyebrow}>
                {MEMBERSHIP_PLAN_ORDER.map((id) => {
                  const priced = planQuote(id);
                  const copy = MEMBERSHIP_COPY.plans[id];
                  if (!priced || !copy) return null;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={plan === id}
                      data-testid={`cb-member-plan-${id}`}
                      className={`cb-member-plan${plan === id ? " is-selected" : ""}`}
                      onClick={() => setPlan(id)}
                    >
                      <span className="cb-member-plan-name">{copy.name}</span>
                      <span className="cb-member-plan-price">
                        {copy.price(formatAed(priced.netFils))}
                      </span>
                      <span className="cb-member-plan-note">{copy.note}</span>
                    </button>
                  );
                })}
              </div>

              {/* The navy number strip: three figures, no ticks. */}
              <div className="cb-member-quota">
                {MEMBERSHIP_COPY.quota.map((q) => (
                  <div key={q.label} className="cb-member-quota-item">
                    <span className="cb-member-quota-value">{q.value ?? memberUnit ?? "—"}</span>
                    <span className="cb-member-quota-label">{q.label}</span>
                  </div>
                ))}
              </div>

              <ul className="cb-member-benefits">
                {MEMBERSHIP_COPY.benefits.map((b) => (
                  <li key={b} className="cb-member-benefit">
                    <span className="cb-member-benefit-mark" aria-hidden="true">
                      &#9670;
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              <PlanSummary planId={plan} />
            </div>

            <div className="cb-buy-foot">
              <button
                type="button"
                className="cb-buy-cta"
                onClick={start}
                data-testid="cb-member-continue"
              >
                {MEMBERSHIP_COPY.cta}
              </button>
              <div className="cb-buy-foot-row">
                <span className="cb-buy-helper">{MEMBERSHIP_COPY.ctaHelper}</span>
                <button type="button" className="cb-buy-quiet" onClick={onClose}>
                  {MEMBERSHIP_COPY.notNow}
                </button>
              </div>
            </div>
          </>
        )}

        {step === "payment" && (
          /* STRIPE ONLY — the widget shows the plan and the total it will
             charge, and a second set of figures beside it can only ever agree
             by luck. */
          <section className="cb-buy-main cb-buy-main--solo">
            <div className="cb-buy-main-head">
              <button
                type="button"
                className="cb-buy-back"
                onClick={back}
                data-testid="cb-member-back"
              >
                &#8592; {MEMBERSHIP_COPY.back}
              </button>
              <button
                type="button"
                className="cb-modal-close"
                onClick={onClose}
                aria-label={MEMBERSHIP_COPY.close}
              >
                &#215;
              </button>
            </div>

            <h3 className="cb-buy-payment-title">{MEMBERSHIP_COPY.paymentTitle}</h3>
            <p className="cb-buy-payment-sub">{MEMBERSHIP_COPY.paymentSub}</p>

            <StripeCheckoutPanel
              clientSecret={clientSecret}
              loadingTitle={MEMBERSHIP_COPY.bridgeTitle}
              loadingSub={MEMBERSHIP_COPY.bridgeSub}
              loadingNote={MEMBERSHIP_COPY.bridgeNote}
              footer={MEMBERSHIP_COPY.stripeFooter}
              testId="cb-member-stripe"
              onComplete={() => {
                setDone(true);
                // The cap is DERIVED from membership, so the page only has to
                // ask again, never be told.
                if (onPaid) onPaid();
              }}
            />
          </section>
        )}

        {step === "done" && (
          <>
            <div className="cb-buy-state" data-testid="cb-member-done">
              <span className="cb-buy-state-mark cb-buy-state-mark--ok" aria-hidden="true">
                &#10003;
              </span>
              <p className="cb-buy-state-eyebrow">{MEMBERSHIP_COPY.successEyebrow}</p>
              <h3 className="cb-buy-state-title">{MEMBERSHIP_COPY.successTitle}</h3>
              <p className="cb-buy-state-body">{MEMBERSHIP_COPY.successBody(companyName)}</p>
              <p className="cb-buy-state-body">{MEMBERSHIP_COPY.successAllowance}</p>
            </div>
            <div className="cb-buy-foot">
              <button type="button" className="cb-buy-cta" onClick={onClose}>
                {MEMBERSHIP_COPY.successAction}
              </button>
            </div>
          </>
        )}

        {step === "conflict" && (
          <>
            <div className="cb-buy-state" role="status" data-testid="cb-member-already">
              <span className="cb-buy-state-mark cb-buy-state-mark--ok" aria-hidden="true">
                &#10003;
              </span>
              <h3 className="cb-buy-state-title">{MEMBERSHIP_COPY.alreadyMemberTitle}</h3>
              <p className="cb-buy-state-body">{MEMBERSHIP_COPY.alreadyMemberBody}</p>
            </div>
            <div className="cb-buy-foot">
              <button type="button" className="cb-buy-cta" onClick={onClose}>
                {MEMBERSHIP_COPY.alreadyMemberAction}
              </button>
            </div>
          </>
        )}

        {step === "error" && (
          <>
            <div className="cb-buy-state" role="alert" data-testid="cb-member-error">
              <span className="cb-buy-state-mark cb-buy-state-mark--warn" aria-hidden="true">
                !
              </span>
              <h3 className="cb-buy-state-title">{MEMBERSHIP_COPY.errorTitle}</h3>
              <p className="cb-buy-state-body">{error?.message || MEMBERSHIP_COPY.errorBody}</p>
            </div>
            <div className="cb-buy-foot cb-buy-foot--pair">
              <button
                type="button"
                className="cb-buy-cta"
                onClick={start}
                data-testid="cb-member-retry"
              >
                {MEMBERSHIP_COPY.tryAgain}
              </button>
              <button type="button" className="cb-buy-secondary" onClick={onClose}>
                {MEMBERSHIP_COPY.notNow}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { PlanSummary };
export default MembershipModal;
